import { createFileRoute } from '@tanstack/react-router';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { getRuntimeConfig } from '@apps/web/lib/runtime_config';

export const Route = createFileRoute('/_app/slack/link')({
    component: SlackLinkPage,
    validateSearch: z.object({
        token: z.string().optional(),
    }),
});

type LinkState =
    | { readonly status: 'linking' }
    | { readonly status: 'linked' }
    | { readonly status: 'failed'; readonly message: string };

function SlackLinkPage() {
    const { token } = Route.useSearch();
    const { apiUrl } = useMemo(() => getRuntimeConfig(), []);
    const [state, setState] = useState<LinkState>({ status: 'linking' });

    useEffect(() => {
        if (!token) {
            setState({
                status: 'failed',
                message: 'The Slack link is missing its token.',
            });
            return;
        }

        const abortController = new AbortController();

        async function linkSlackAccount() {
            try {
                const response = await fetch(`${apiUrl}/oauth/slack/link`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token }),
                    signal: abortController.signal,
                });
                if (!response.ok) {
                    let message = 'Phoebe could not link this Slack account.';
                    try {
                        const body = (await response.json()) as {
                            readonly detail?: unknown;
                        };
                        if (typeof body.detail === 'string') {
                            message = body.detail;
                        }
                    } catch {
                        // Keep the generic message if the API returned HTML or
                        // another non-JSON error body.
                    }
                    setState({ status: 'failed', message });
                    return;
                }
                setState({ status: 'linked' });
            } catch (error) {
                if (abortController.signal.aborted) {
                    return;
                }
                setState({
                    status: 'failed',
                    message:
                        error instanceof Error
                            ? error.message
                            : 'Phoebe could not reach the API.',
                });
            }
        }

        void linkSlackAccount();

        return () => abortController.abort();
    }, [apiUrl, token]);

    return (
        <main className="mx-auto flex min-h-[60vh] max-w-xl flex-col justify-center px-6 py-12">
            {state.status === 'linking' ? (
                <StatusView
                    icon={<Loader2 className="size-8 animate-spin" />}
                    title="Linking Slack"
                    body="Connecting this Slack identity to your Phoebe account."
                />
            ) : null}
            {state.status === 'linked' ? (
                <StatusView
                    icon={<CheckCircle2 className="size-8 text-green-600" />}
                    title="Slack linked"
                    body="Return to Slack and run /phoebe help to see what you can do."
                />
            ) : null}
            {state.status === 'failed' ? (
                <StatusView
                    icon={<XCircle className="size-8 text-red-600" />}
                    title="Link failed"
                    body={state.message}
                />
            ) : null}
        </main>
    );
}

function StatusView({
    icon,
    title,
    body,
}: {
    readonly icon: ReactNode;
    readonly title: string;
    readonly body: string;
}) {
    return (
        <div className="flex flex-col items-center text-center">
            <div className="text-muted-foreground mb-4">{icon}</div>
            <h1 className="text-2xl font-semibold">{title}</h1>
            <p className="text-muted-foreground mt-3 text-sm">{body}</p>
        </div>
    );
}
